import React, { useState, useEffect, useMemo, useCallback } from 'react';
import GridLayout, { WidthProvider } from 'react-grid-layout';

// UTILS
import { formatTileData } from 'utils/formatTileData';
import iconMap from 'utils/iconMap';

// COMPONENTS
import InformationTile from './tiles/informationTileComponennt';
import LinkTile from './tiles/linkTileComponent';
import NotificationTile from './tiles/notificationTileComponent';
import ButtonTile from './tiles/buttonTileComponent';
import CountdownTile from './tiles/countdownTileComponent';

const ReactGridLayout = WidthProvider(GridLayout);

const GridEditing = ({ user, tiles, layout, setLayout, isEditing, isDesktopMode, removeFromGrid, setColorPickerTileId }) => {
    const [draggedItem, setDraggedItem] = useState(null);
    const [resizingItem, setResizingItem] = useState(null);
    const [selectedTileId, setSelectedTileId] = useState(null);
    const [isDarkMode, setIsDarkMode] = useState(false);
    const isResizable = true;

    const MinusIcon = iconMap.minus;
    //const FillIcon = iconMap.fill;
    const HeadbandNewIcon = iconMap['headbandNew'];
    
    const desktopConfig = {
        cols: 10,
        tileWidth: 120,
        tileHeight: 120,
        margin: 10
    };

    const mobileConfig = {
        cols: 2,
        tileWidth: 150,
        tileHeight: 150,
        margin: 10
    };

    const config = isDesktopMode ? desktopConfig : mobileConfig;

    const { cols, tileWidth, tileHeight, margin } = config;
    const rowHeight = tileHeight;
    const marginArray = [margin, margin];
    const width = (cols * tileWidth) + ((cols + 1) * margin);

    const [gridDimensions, setGridDimensions] = useState({ width: width, height: 750 });

    useEffect(() => {
        if (!layout) return;
        const maxY = Math.max(...layout.map(item => item.y + item.h), 0);
        const totalHeight = (maxY + 1) * rowHeight + (maxY + 2) * margin;
        setGridDimensions({ width, height: totalHeight });
    }, [layout, rowHeight, width, margin]);    

    const formattedTiles = useMemo(() => {
        return tiles.map(tile => formatTileData(tile));
    }, [tiles]);

    const MAX_HISTORY = 50;
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);

    const handleLayoutChange = useCallback(
        (newLayout) => {
            if (JSON.stringify(newLayout) === JSON.stringify(layout)) return;

            setUndoStack(prev => {
                const updated = [...prev, layout];
                return updated.length > MAX_HISTORY ? updated.slice(1) : updated;
            });
            setRedoStack([]);
            setLayout(newLayout);
        },
        [layout, setLayout]
    );

    const undo = () => {
        if (undoStack.length === 0) return;
        const previousLayout = undoStack[undoStack.length - 1];
        setUndoStack(prev => prev.slice(0, -1));
        setRedoStack(prev => [layout, ...prev]);
        setLayout(previousLayout);
    };

    const redo = () => {
        if (redoStack.length === 0) return;
        const nextLayout = redoStack[0];
        setRedoStack(prev => prev.slice(1));
        setUndoStack(prev => [...prev, layout]);
        setLayout(nextLayout);
    };

    useEffect(() => {
        const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
                undo();
            } else if ((e.ctrlKey || e.metaKey) && e.key === 'y') {
                redo();
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [undo, redo, layout, undoStack, redoStack]);

    const handleDragStart = (_, oldItem) => setDraggedItem(oldItem.i);
    const handleDragStop = () => setDraggedItem(null);
    const handleResizeStart = (_, oldItem) => setResizingItem(oldItem.i);
    const handleResizeStop = () => setResizingItem(null);    

    const renderTileContent = (tile) => {
        switch (tile.type) {
            case 'information':
                return <InformationTile icon={tile.data.icon} value={tile.data.value} unit={tile.data.unit} isEditing={isEditing} />;
            case 'link':
                return <LinkTile user_id={user?.userBDDInfo.data[0].USR_ID} tile_id={tile.id} title={tile.data.title} desc={tile.data.description} url={tile.data.url} imageUrl={tile.data.image_url} isEditing={isEditing} userEmail={user?.userADInfo?.data?.ad_ai?.uaiad_mail} currentBreakpoint={isDesktopMode ? 'lg' : 'xxs'} />;
            case 'notification':
                return <NotificationTile title={tile.data.title} url={tile.data.url} notif={tile.notification} isEditing={isEditing} />;
            case 'button':
                return <ButtonTile title={tile.data.title} data={tile.data} folderColor={tile.color} isEditing={isEditing} />;
            case 'countdown':
                return <CountdownTile title={tile.data.title} event={tile.evenement} isEditing={isEditing} />;
            default:
                return <div>Erreur: Type de tuile inconnu</div>;
        }
    };

    const handleRemoveTile = (tileId) => {
        removeFromGrid(tileId);
    };
    
    useEffect(() => {
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };

        // Vérifier initialement
        checkDarkMode();

        // Observer les changements de classe sur l'élément html
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    if (!tiles || tiles.length === 0 || !layout) return null;

    return (
        <div
            style={{
                width: gridDimensions.width,
                height: gridDimensions.height,
                backgroundImage: `
                    linear-gradient(to right, ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} 1px, transparent 1px),
                    linear-gradient(to bottom, ${isDarkMode ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)'} 1px, transparent 1px)
                `,
                backgroundSize: `${tileWidth + margin}px ${tileHeight + margin}px`,
                backgroundPosition: `${margin / 2}px ${margin / 2}px`
            }}
            className="select-none relative top-0 left-0 m-auto"
        >
            <ReactGridLayout
                className={`layout ${isResizable ? 'is-resizable' : 'not-resizable'}`}
                layout={layout}
                cols={cols}
                rowHeight={rowHeight}
                margin={marginArray}
                isDraggable={true}
                isResizable={isResizable}
                useCSSTransforms={true}
                onLayoutChange={handleLayoutChange}
                onDragStart={handleDragStart}
                onDragStop={handleDragStop}
                onResizeStart={handleResizeStart}
                onResizeStop={handleResizeStop}
                draggableCancel=".no-drag"
            >
                {formattedTiles.map(tile => (
                <div 
                    key={tile.id} 
                    className={`relative rounded-3xl ${
                        resizingItem === tile.id.toString() || draggedItem === tile.id.toString() ? 'z-[1000] shadow-2xl cursor-grabbing is-dragged' : 'cursor-grab'
                    }`}
                >
                    <div
                        className={`flex justify-center rounded-20 items-center w-full h-full transition-transform duration-200 ease-in-out
                            ${resizingItem === tile.id.toString() || draggedItem === tile.id.toString() ? 'shadow-2xl' : ''}
                            ${selectedTileId === tile.id ? 'shadow-2xl' : ''}
                        `}
                        style={{ backgroundColor: tile.color }}
                        onClick={() => { setSelectedTileId(tile.id); setColorPickerTileId(tile.id); }}
                    >
                        <div 
                            className={`absolute inset-0 rounded-20 pointer-events-none
                            ${selectedTileId === tile.id ? 'border-3 border-blanc animate-blink' : ''}`}
                        />
                        {tile.isOptional && (
                            <div 
                                className="absolute -top-1 -left-1 bg-blanc rounded-full cursor-pointer no-drag z-10" 
                                onMouseDown={(e) => e.stopPropagation()} 
                                onTouchStart={(e) => e.stopPropagation()} 
                                onClick={() => handleRemoveTile(tile.id)}
                            >
                                <MinusIcon className="size-5 fill-noir" />
                            </div>
                        )}
                        {/* <div className="absolute top-2 right-2 p-2 rounded-full cursor-pointer" onClick={() => setColorPickerTileId(tile.id)}>
                            <FillIcon className="h-4 w-4 fill-blanc" />
                        </div> */}
                        {/* Affiche une icône de "nouveau" si la tuile a été créée il y a moins de 10 jours */}
                        {tile.createdAt && new Date() - new Date(tile.createdAt) < 864000000 && (
                            <div className="absolute top-1 left-1">
                            <HeadbandNewIcon className="size-10 lg:size-8 2xl:size-10 fill-blanc" />
                            </div>
                        )}
                        {renderTileContent(tile)}
                    </div>
                </div>  
                ))}
            </ReactGridLayout>
        </div>
    );
};

export default GridEditing;